/**
 * Custom element that adds click-and-drag scrolling to the predictive search queries list
 */
class PredictiveSearchQueries extends HTMLElement {
  #isDown = false;
  #startX = 0;
  #scrollLeft = 0;
  #hasMoved = false;

  connectedCallback() {
    // Set initial cursor style
    this.style.cursor = 'grab';
    
    this.addEventListener('mousedown', this.#handleMouseDown);
    this.addEventListener('mouseleave', this.#handleMouseLeave);
    this.addEventListener('mouseup', this.#handleMouseUp);
    this.addEventListener('mousemove', this.#handleMouseMove);
    
    // Prevent drag on links during dragging - use capture phase
    this.addEventListener('click', this.#handleClick, { capture: true });
    
    // Prevent default drag behavior on links
    const links = this.querySelectorAll('a');
    links.forEach(link => {
      link.addEventListener('dragstart', (e) => e.preventDefault());
    });
  }

  disconnectedCallback() {
    this.removeEventListener('mousedown', this.#handleMouseDown);
    this.removeEventListener('mouseleave', this.#handleMouseLeave);
    this.removeEventListener('mouseup', this.#handleMouseUp);
    this.removeEventListener('mousemove', this.#handleMouseMove);
    this.removeEventListener('click', this.#handleClick, { capture: true });
  }

  #handleMouseDown = (e) => {
    // Only handle left mouse button
    if (e.button !== 0) return;
    
    // Don't prevent default on the initial mousedown to allow proper event flow
    this.#isDown = true;
    this.#hasMoved = false;
    this.#startX = e.pageX;
    this.#scrollLeft = this.scrollLeft;
    this.style.cursor = 'grabbing';
    this.style.userSelect = 'none';
    
    // Add document-level listeners for move and up
    document.addEventListener('mousemove', this.#handleMouseMove);
    document.addEventListener('mouseup', this.#handleMouseUp);
  };

  #handleMouseLeave = () => {
    if (!this.#isDown) {
      this.style.cursor = 'grab';
    }
  };

  #handleMouseUp = () => {
    this.#isDown = false;
    this.style.cursor = 'grab';
    this.style.userSelect = '';
    
    // Remove document-level listeners
    document.removeEventListener('mousemove', this.#handleMouseMove);
    document.removeEventListener('mouseup', this.#handleMouseUp);
    
    // Reset hasMoved after a short delay to allow click event to check it
    setTimeout(() => {
      this.#hasMoved = false;
    }, 50);
  };

  #handleMouseMove = (e) => {
    if (!this.#isDown) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const x = e.pageX;
    const walk = (x - this.#startX) * 1.5; // Scroll speed multiplier
    
    // Mark as moved if we've dragged more than a few pixels
    if (Math.abs(walk) > 3) {
      this.#hasMoved = true;
    }
    
    this.scrollLeft = this.#scrollLeft - walk;
  };

  #handleClick = (e) => {
    // Prevent navigation if user was dragging
    if (this.#hasMoved) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      return false;
    }
  };
}

if (!customElements.get('predictive-search-queries')) {
  customElements.define('predictive-search-queries', PredictiveSearchQueries);
}

